import { Config } from "@config";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, Button, PromisePool } from "bm-ui-kit";
import deburr from "lodash/deburr";
import groupBy from "lodash/groupBy";
import type { ReactNode } from "react";
import React, { Fragment } from "react";
import type { DropdownProps } from "react-bootstrap";
import { AsyncTypeahead, Highlighter, Menu, MenuItem } from "react-bootstrap-typeahead";
import "./style.scss";

type SubType = "post" | "page" | "category" | "post_tag" | "bm_better_to_know";
interface Suggestions {
	id: number | string;
	title: string;
	url: string;
	type: "post" | "term" | "post-format";
	subtype: SubType;
}

interface Props {
	search?: string;
	placeholder?: string;
	subtypeFilter?: SubType;
	showAllUrl?: string;
}

interface State {
	suggestions: Suggestions[];
	isLoading: boolean;
}

export default class SearchComp extends React.Component<Props, State> {
	private readonly promisePool = new PromisePool();

	public constructor(props: Props) {
		super(props);

		this.state = { suggestions: [], isLoading: false };
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	@autobind
	private onSearch(value: string): void {
		const { subtypeFilter } = this.props;

		this.setState({
			isLoading: true,
		});

		this.promisePool
			.add(
				http.get(
					Config.getBankmonitorUrl("/wp-json/wp/v2/search-content", {
						search: value,
						subtype: subtypeFilter,
					}),
				),
			)
			.then((suggestions: Suggestions[]) => {
				this.setState({
					suggestions,
				});
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			})
			.finally(() => {
				this.setState({
					isLoading: false,
				});
			});
	}

	public render(): ReactNode {
		const { isLoading, suggestions } = this.state;
		const { placeholder = "Adj meg egy keresési szót!" } = this.props;

		return (
			<AsyncTypeahead
				id="searchInput"
				minLength={3}
				filterBy={() => true}
				renderMenu={this.renderMenu}
				labelKey={(option) => option.title}
				onSearch={this.onSearch}
				onChange={this.onChange}
				promptText="Adj meg egy keresési szót..."
				searchText="Keresés..."
				emptyLabel="Nincs találat."
				options={suggestions}
				isLoading={isLoading}
				placeholder={placeholder}
				autoFocus
			/>
		);
	}

	@autobind
	private renderMenu(results: Suggestions[], menuProps: DropdownProps, state): ReactNode {
		const { subtypeFilter, showAllUrl = `/?s={search}` } = this.props;

		let index = 0;
		const groupedResults = groupBy(results, "subtype");

		if (Object.keys(groupedResults).length === 0) {
			return null;
		}

		return (
			<Menu {...menuProps} maxHeight="600px">
				{this.renderSmartResult(state.text)}
				{Object.keys(groupedResults)
					.sort()
					.map((group) => (
						<Fragment key={group}>
							{index !== 0 && <Menu.Divider />}
							{subtypeFilter === undefined && this.renderGroupHeader(group)}
							{groupedResults[group].map((item) => (
								<MenuItem
									className="small text-truncate"
									key={item.id}
									option={item}
									position={++index}
									title={item.title}
								>
									<Highlighter search={state.text}>{item.title}</Highlighter>
								</MenuItem>
							))}
						</Fragment>
					))}
				<Menu.Divider />
				<MenuItem
					className="small text-truncate"
					key="show-all"
					option={{
						id: -1,
						title: "Mutasd mind!",
						url: showAllUrl.replace("{search}", encodeURIComponent(state.text)),
						type: "term",
					}}
					position={++index}
				>
					Mutasd mind!
				</MenuItem>
			</Menu>
		);
	}

	private renderGroupHeader(groupName: string): ReactNode {
		switch (groupName) {
			case "page":
				return (
					<Menu.Header>
						<strong>Oldalak:</strong>
					</Menu.Header>
				);
			case "post":
				return (
					<Menu.Header>
						<strong>Cikkek:</strong>
					</Menu.Header>
				);
			case "bm_better_to_know":
				return (
					<Menu.Header>
						<strong>Jobb tudni:</strong>
					</Menu.Header>
				);
			case "bm_job_offer":
				return (
					<Menu.Header>
						<strong>Karrier:</strong>
					</Menu.Header>
				);
			default:
				return null;
		}
	}

	private renderSmartResult(searchText: string): ReactNode {
		if (this.isRelevant(searchText, ["lakashitel", "jelzalog"])) {
			return (
				<MenuItem
					className="text-truncate bg-light py-3"
					option={{
						id: -1,
						title: "Lakáshitel kalkulátor",
						url: `/lakashitel-kalkulator/`,
						type: "term",
					}}
					position={0}
				>
					<div className="h5">
						<Highlighter search={searchText}>Lakáshitel kalkulátor</Highlighter>
					</div>
					<p className="small text-wrap mb-2">
						Azonnal láthatod, hogy a szükséges hitelösszeg milyen havi törlesztőt jelent majd a számodra.
					</p>
					<Button className="ml-auto" size="sm">
						Kalkulátor megnyitása
					</Button>
				</MenuItem>
			);
		}

		if (this.isRelevant(searchText, ["bankszamla"])) {
			return (
				<MenuItem
					className="text-truncate bg-light py-3"
					option={{
						id: -1,
						title: "Bankszámla kalkulátor",
						url: `/bankszamla/`,
						type: "term",
					}}
					position={0}
				>
					<div className="h5">
						<Highlighter search={searchText}>Bankszámla kalkulátor</Highlighter>
					</div>
					<p className="small text-wrap mb-2">
						Segítünk megtalálni a legjobb bankszámlát gyorsan és egyszerűen.
					</p>
					<Button className="ml-auto" size="sm">
						Kalkulátor megnyitása
					</Button>
				</MenuItem>
			);
		}

		if (this.isRelevant(searchText, ["szemelyi"])) {
			return (
				<MenuItem
					className="text-truncate bg-light py-3"
					option={{
						id: -1,
						title: "Személyi kölcsön kalkulátor",
						url: `/szemelyi-kolcson/`,
						type: "term",
					}}
					position={0}
				>
					<div className="h5">
						<Highlighter search={searchText}>Személyi kölcsön kalkulátor</Highlighter>
					</div>
					<p className="small text-wrap mb-2">
						14 bank szabad felhasználású, fix kamatú szeméi kölcsöneinek összehasonlítása pillanatok alatt.
					</p>
					<Button className="ml-auto" size="sm">
						Kalkulátor megnyitása
					</Button>
				</MenuItem>
			);
		}

		return null;
	}

	@autobind
	private onChange(value: Suggestions[]): void {
		if (value[0]?.url) {
			window.location.href = value[0].url;
		}
	}

	private isRelevant(search: string, keywords: string[]): boolean {
		const cleanText = deburr(search).toLocaleLowerCase().replace(/\s+/g, "");

		return !!keywords.find((keyword) => {
			if (cleanText.includes(keyword) || keyword.includes(cleanText)) {
				return true;
			}

			return false;
		});
	}
}
